import type { DeviceContext } from '../types'
import { scrubCredentials } from './scrub'
import { sanitizeUrl } from './urlSanitizer'

export function collectDevice(): DeviceContext {
  const nav = navigator as Navigator & { connection?: { effectiveType?: string }; deviceMemory?: number }
  const connection = nav.connection?.effectiveType
  const deviceMemory = nav.deviceMemory
  // Auth flows commonly hand off via query-string tokens (`?token=…`,
  // `?code=…`); when the user clicks through to the host page, both the
  // raw referrer AND the host's own pathname can carry those values.
  // Run both through the same sanitizer used for fetch/XHR; collapse the
  // result back to a path-only string so we don't accidentally upload the
  // operator-side host's origin as if it were the captured page's URL.
  const rawReferrer = document.referrer || undefined
  const referrer = rawReferrer !== undefined ? sanitizeUrl(rawReferrer) : undefined
  const sanitizedHere = sanitizeUrl(window.location.pathname + window.location.search)
  let pathname: string
  try {
    const parsed = new URL(sanitizedHere, window.location.origin)
    pathname = parsed.pathname + parsed.search
  } catch {
    pathname = window.location.pathname
  }
  return {
    viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 },
    screen: { w: window.screen.width, h: window.screen.height },
    platform: nav.platform,
    language: nav.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    ...(connection !== undefined && { connection }),
    online: nav.onLine,
    ...(deviceMemory !== undefined && { deviceMemory }),
    hardwareConcurrency: nav.hardwareConcurrency,
    ...(referrer !== undefined && { referrer }),
    // Hosts commonly stuff credentials / PII into page titles
    // ("Inbox (3) — bob@x.com", "Reset password — token=abc"). Cap to a
    // sane length and run through the same scrubber the console/error
    // paths use.
    title: scrubCredentials((document.title || '')).slice(0, 200),
    pathname,
  }
}
